import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Layers, TrendingUp, AlertTriangle, Building2, Factory, Zap, Cpu, Wrench, Check, ChevronDown } from 'lucide-react';
import { getDatabaseAlerts, getMineralSectorImpact, getMineralSectorImpactAvailableData } from '../services/criticalMineralsApi';
import { getSupplierRisk, getConsumerRisk, getComprehensivePartnerRisk } from '../services/partnerRiskApi';

const MineralSectorTreeMap = () => {
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, data: null, x: 0, y: 0 });
  const [alerts, setAlerts] = useState([]);
  const [mineralSectorData, setMineralSectorData] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMineral, setSelectedMineral] = useState(null);
  
  // New state for TreeMap modes
  const [treemapMode, setTreemapMode] = useState('mineral-sector');
  const [treemapData, setTreemapData] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // State for year and mineral selection
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedMineralFilter, setSelectedMineralFilter] = useState('Lithium'); // Default to first mineral
  const [availableYears, setAvailableYears] = useState(['2024', '2023', '2022', '2021', '2020']);
  const [availableMinerals, setAvailableMinerals] = useState(['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
  const [availableMineralsWithAll, setAvailableMineralsWithAll] = useState(['All', 'Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMineralDropdown, setShowMineralDropdown] = useState(false);
  
  const svgRef = useRef();
  const containerRef = useRef();

  // TreeMap mode options - only Mineral-Sector Impact
  const treemapModes = [
    { value: 'mineral-sector', label: 'Mineral-Sector Impact' }
  ];

  // Hierarchical data structure for D3 TreeMap

  // Transform mineral sector data into tree map format
  const transformDataToTreeMap = (mineralData, selectedIndustries) => {
    if (!mineralData || mineralData.length === 0) {
      return {
        name: "Mineral-Sector Impact",
        children: []
      };
    }

    // Check if selectedIndustries is valid and not empty
    if (!selectedIndustries || selectedIndustries.length === 0) {
      return {
        name: "Mineral-Sector Impact",
        children: []
      };
    }

    // Filter minerals by selected industries
    const filteredMineralData = mineralData.filter(mineral => 
      selectedIndustries.includes(mineral.primary_industry)
    );

    if (filteredMineralData.length === 0) {
      return {
        name: "Mineral-Sector Impact",
        children: []
      };
    }

    // Group minerals by their primary industry
    const industryGroups = {};
    
    filteredMineralData.forEach(mineral => {
      const primaryIndustry = mineral.primary_industry || 'Other Industries';
      
      if (!industryGroups[primaryIndustry]) {
        industryGroups[primaryIndustry] = [];
      }
      
      // Map risk levels to numeric values for visualization
      const riskValueMap = {
        'Critical': 95,
        'High': 80,
        'Medium': 60,
        'Low': 40
      };
      
      industryGroups[primaryIndustry].push({
        name: mineral.mineral_name,
        value: riskValueMap[mineral.supply_chain_risk_level] || 50,
        riskLevel: mineral.supply_chain_risk_level?.toLowerCase() || 'medium',
        applications: mineral.major_applications,
        signals: Math.floor((mineral.mineral_name.length + (mineral.supply_chain_risk_level?.length || 0)) * 0.8) + 1 // Deterministic signal count based on mineral name and risk level
      });
    });

    // Convert to tree map structure
    const children = Object.entries(industryGroups).map(([industry, minerals]) => ({
      name: industry,
      children: minerals
    }));

    return {
      name: "Mineral-Sector Impact",
      children: children
    };
  };

  // Transform import distribution data into tree map format
  const transformImportDistributionData = (data) => {
    console.log('Transform import distribution data called with:', data);
    
    // Debug: Log all USA-related data
    if (data && Array.isArray(data)) {
      const usaData = data.filter(item => 
        item.source_country === 'USA' || 
        item.source_country === 'United States' ||
        (item.source_country && item.source_country.toLowerCase().includes('usa'))
      );
      if (usaData.length > 0) {
        console.log('DEBUG: Found USA data in import distribution:', usaData);
      }
    }
    
    // Handle different data formats
    let dataArray = [];
    
    if (Array.isArray(data)) {
      dataArray = data;
    } else if (data && data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else if (data && data.success && data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else {
      console.log('No valid data array found for import distribution:', data);
      return { name: "Import Distribution", children: [] };
    }

    console.log('Data array after processing:', dataArray);
    
    if (dataArray.length === 0) {
      console.log('Data array is empty, returning empty tree map');
      return { name: "Import Distribution", children: [] };
    }

    // Group by mineral category, then by mineral, then by source country
    const categoryGroups = {};
    
    dataArray.forEach(item => {
      const category = item.mineral_category || 'Other';
      const mineral = item.mineral_name;
      const country = item.source_country;
      const importShare = parseFloat(item.india_import_share_percentage) || 0;
      const importVolume = parseFloat(item.total_india_import_metric_ton) || 0;
      
      // Determine risk level based on import dependency percentage
      let riskLevel = 'low';
      if (importShare >= 70) {
        riskLevel = 'high';
      } else if (importShare >= 40) {
        riskLevel = 'medium';
      }
      
      // Debug logging for USA specifically
      if (country === 'USA' || country === 'United States') {
        console.log(`DEBUG: USA risk level calculation:`, {
          country: country,
          importShare: importShare,
          calculatedRiskLevel: riskLevel,
          rawData: item
        });
      }
      
      if (!categoryGroups[category]) {
        categoryGroups[category] = {};
      }
      
      if (!categoryGroups[category][mineral]) {
        categoryGroups[category][mineral] = [];
      }
      
      // Check if this country already exists in this mineral category
      const existingCountryIndex = categoryGroups[category][mineral].findIndex(
        existing => existing.name === country
      );
      
      if (existingCountryIndex >= 0) {
        // Aggregate with existing country data
        const existing = categoryGroups[category][mineral][existingCountryIndex];
        const aggregatedVolume = existing.volume + importVolume;
        const aggregatedShare = Math.max(existing.importPercentage, importShare); // Use higher percentage
        const aggregatedRiskLevel = aggregatedShare >= 70 ? 'high' : 
                                   aggregatedShare >= 40 ? 'medium' : 'low';
        
        categoryGroups[category][mineral][existingCountryIndex] = {
          ...existing,
          value: aggregatedVolume,
          volume: aggregatedVolume,
          riskLevel: aggregatedRiskLevel,
          importPercentage: aggregatedShare,
          dependencyLevel: aggregatedShare
        };
        
        // Debug logging for USA aggregation
        if (country === 'USA' || country === 'United States') {
          console.log(`DEBUG: Aggregated USA data:`, {
            country: country,
            originalShare: existing.importPercentage,
            newShare: importShare,
            aggregatedShare: aggregatedShare,
            originalRiskLevel: existing.riskLevel,
            aggregatedRiskLevel: aggregatedRiskLevel
          });
        }
      } else {
        // Add new country data
        const countryData = {
          name: country,
          value: importVolume, // Use import volume (quantity) for TreeMap sizing instead of percentage
          volume: importVolume,
          riskLevel: riskLevel,
          importPercentage: importShare,
          price: item.current_price_usd_per_ton,
          dependencyLevel: importShare // Store dependency level for color coding
        };
        
        // Debug logging for USA specifically
        if (country === 'USA' || country === 'United States') {
          console.log(`DEBUG: Adding new USA country data:`, {
            country: country,
            importShare: importShare,
            riskLevel: riskLevel,
            countryData: countryData,
            category: category,
            mineral: mineral
          });
        }
        
        categoryGroups[category][mineral].push(countryData);
      }
    });

    const children = Object.entries(categoryGroups).map(([category, minerals]) => ({
      name: category,
      children: Object.entries(minerals).map(([mineral, countries]) => ({
        name: mineral,
        children: countries
      }))
    }));

    // Debug: Log final structure with USA data
    const finalStructure = { name: "Import Distribution", children };
    const usaInFinal = JSON.stringify(finalStructure).includes('"name":"USA"') || 
                      JSON.stringify(finalStructure).includes('"name":"United States"');
    if (usaInFinal) {
      console.log('DEBUG: Final import distribution structure contains USA:', finalStructure);
    }

    return finalStructure;
  };

  // Transform export distribution data into tree map format
  const transformExportDistributionData = (data) => {
    // Handle different data formats
    let dataArray = [];
    
    if (Array.isArray(data)) {
      dataArray = data;
    } else if (data && data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else if (data && data.success && data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else {
      console.log('No valid data array found for export distribution:', data);
      return { name: "Export Distribution", children: [] };
    }

    if (dataArray.length === 0) {
      return { name: "Export Distribution", children: [] };
    }

    // Group by mineral category, then by mineral, then by export country
    const categoryGroups = {};
    
    dataArray.forEach(item => {
      const category = item.mineral_category || 'Other';
      const mineral = item.mineral_name;
      const country = item.export_country;
      const exportShare = item.export_percentage || 0;
      const exportValue = item.export_value || 0;
      
      if (!categoryGroups[category]) {
        categoryGroups[category] = {};
      }
      
      if (!categoryGroups[category][mineral]) {
        categoryGroups[category][mineral] = [];
      }
      
      categoryGroups[category][mineral].push({
        name: country,
        value: exportShare,
        volume: exportValue,
        riskLevel: 'medium', // Export risk would need separate calculation
        exportPercentage: exportShare,
        exportValue: exportValue
      });
    });

    const children = Object.entries(categoryGroups).map(([category, minerals]) => ({
      name: category,
      children: Object.entries(minerals).map(([mineral, countries]) => ({
        name: mineral,
        children: countries
      }))
    }));

    return { name: "Export Distribution", children };
  };

  // Transform partner risk data into tree map format
  const transformPartnerRiskData = (data) => {
    // Handle different data formats
    let dataArray = [];
    
    if (Array.isArray(data)) {
      dataArray = data;
    } else if (data && data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else if (data && data.success && data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else {
      console.log('No valid data array found for partner risk:', data);
      return { name: "Partner Risk Distribution", children: [] };
    }

    if (dataArray.length === 0) {
      return { name: "Partner Risk Distribution", children: [] };
    }

    // Group by risk level, then by country, then by mineral
    const riskGroups = {};
    
    dataArray.forEach(item => {
      const riskLevel = item.risk_level || 'MEDIUM RISK';
      const country = item.country || item.partner_country_name;
      const mineral = item.mineral_name;
      const riskScore = item.partner_risk_score || item.final_risk_score || 0;
      const tradeVolume = item.trade_volume || 0;
      const indiaRole = item.india_role || 'Trading Partner';
      
      if (!riskGroups[riskLevel]) {
        riskGroups[riskLevel] = {};
      }
      
      if (!riskGroups[riskLevel][country]) {
        riskGroups[riskLevel][country] = [];
      }
      
      riskGroups[riskLevel][country].push({
        name: mineral,
        value: riskScore,
        tradeVolume: tradeVolume,
        indiaRole: indiaRole,
        riskLevel: riskLevel.toLowerCase(),
        supplyChainRisk: item.supply_chain_risk || item.partner_risk_score || 0,
        marketDemandRisk: item.market_demand_risk || item.partner_risk_score || 0,
        wgiScore: item.wgi_score || item.avg_normalised_wgi || 0,
        fsiScore: item.fsi_score || item.normalised_fsi || 0,
        basePartnerRisk: item.base_partner_risk || item.avg_fsi_wgi || 0
      });
    });

    const children = Object.entries(riskGroups).map(([riskLevel, countries]) => ({
      name: riskLevel,
      children: Object.entries(countries).map(([country, minerals]) => ({
        name: country,
        children: minerals
      }))
    }));

    return { name: "Partner Risk Distribution", children };
  };

  // Transform mineral signals data into tree map format
  const transformMineralSignalsData = (alerts) => {
    // Handle different data formats
    let dataArray = [];
    
    if (Array.isArray(alerts)) {
      dataArray = alerts;
    } else if (alerts && alerts.data && Array.isArray(alerts.data)) {
      dataArray = alerts.data;
    } else if (alerts && alerts.success && alerts.data && Array.isArray(alerts.data)) {
      dataArray = alerts.data;
    } else {
      console.log('No valid data array found for mineral signals:', alerts);
      return { name: "Mineral-wise Signals", children: [] };
    }

    if (dataArray.length === 0) {
      return { name: "Mineral-wise Signals", children: [] };
    }

    // Group by PESTLE category, then by mineral, then by signal type
    const pestleGroups = {};
    
    dataArray.forEach(alert => {
      const pestleCategory = alert.pestleCategory || 'economic';
      const mineral = alert.mineral || 'General';
      const signalType = alert.signal_type || 'General Alert';
      const riskScore = alert.risk_score || 0.5;
      const impactLevel = alert.impact_level || 'Medium';
      
      if (!pestleGroups[pestleCategory]) {
        pestleGroups[pestleCategory] = {};
      }
      
      if (!pestleGroups[pestleCategory][mineral]) {
        pestleGroups[pestleCategory][mineral] = {};
      }
      
      if (!pestleGroups[pestleCategory][mineral][signalType]) {
        pestleGroups[pestleCategory][mineral][signalType] = {
          count: 0,
          totalRisk: 0,
          maxImpact: impactLevel
        };
      }
      
      pestleGroups[pestleCategory][mineral][signalType].count += 1;
      pestleGroups[pestleCategory][mineral][signalType].totalRisk += riskScore;
    });

    const children = Object.entries(pestleGroups).map(([pestleCategory, minerals]) => ({
      name: pestleCategory.charAt(0).toUpperCase() + pestleCategory.slice(1),
      children: Object.entries(minerals).map(([mineral, signalTypes]) => ({
        name: mineral,
        children: Object.entries(signalTypes).map(([signalType, data]) => ({
          name: signalType,
          value: data.count,
          riskScore: data.totalRisk / data.count,
          impactLevel: data.maxImpact.toLowerCase(),
          signalCount: data.count
        }))
      }))
    }));

    return { name: "Mineral-wise Signals", children };
  };

  // Fetch data from critical_minerals_dependency table
  const fetchCriticalMineralsDependencyData = async (year, mineral) => {
    try {
      const response = await fetch(`/api/critical-minerals-dependency?year=${year}&mineral=${mineral === 'All' ? '' : mineral}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Critical minerals dependency API response:', data);
      
      // Handle different response formats
      if (data && data.success && Array.isArray(data.data)) {
        return data.data;
      } else if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      } else {
        console.warn('Unexpected data format from critical minerals dependency API:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching critical minerals dependency data:', error);
      return [];
    }
  };

  // Fetch available years and minerals from mineral sector impact API
  const fetchAvailableYearsAndMinerals = async () => {
    try {
      const response = await fetch('/api/mineral-sector-impact/available-data');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Available years and minerals API response:', data);
      
      if (data && data.success) {
        if (data.years && Array.isArray(data.years)) {
          const sortedYears = data.years.sort((a, b) => b - a); // Sort descending
          setAvailableYears(sortedYears);
          if (sortedYears.length > 0 && !availableYears.includes(selectedYear)) {
            setSelectedYear(sortedYears[0].toString());
          }
        }
        
        if (data.minerals && Array.isArray(data.minerals)) {
          setAvailableMinerals(data.minerals); // Use API response directly (no 'All' or 'Other Minerals')
        }
      } else {
        // Fallback to default values
        const currentYear = new Date().getFullYear();
        const fallbackYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];
        setAvailableYears(fallbackYears);
        setAvailableMinerals(['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
      }
    } catch (error) {
      console.error('Error fetching available years and minerals:', error);
      // Fallback to default values
      const currentYear = new Date().getFullYear();
      const fallbackYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];
      setAvailableYears(fallbackYears);
      setAvailableMinerals(['All', 'Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
    }
  };

  // Get filtered years based on current mode
  const getFilteredYears = () => {
    if (treemapMode === 'partner-risk') {
      // For partner-risk mode, exclude 2024 as there's no data
      return availableYears.filter(year => year !== 2024);
    }
    return availableYears;
  };

  // Fetch data based on TreeMap mode
  const fetchDataForMode = async (mode, year = selectedYear, mineral = selectedMineralFilter) => {
      setLoading(true);
      try {
      let data = [];
      
      switch (mode) {
        case 'mineral-sector':
          const sectorResponse = await getMineralSectorImpact(mode, year, mineral);
          if (sectorResponse.success && sectorResponse.data) {
            data = sectorResponse.data;
            setMineralSectorData(data);
          
          // Extract unique industries for filter options from the TreeMap structure
            const industries = [];
            if (data.children && Array.isArray(data.children)) {
              data.children.forEach(industry => {
                if (industry.name) {
                  industries.push(industry.name);
                }
              });
            }
            setAvailableIndustries(industries);
            setSelectedIndustries(industries);
          }
          break;
          
        case 'import-distribution':
        case 'export-distribution':
        case 'partner-risk':
          const response = await getMineralSectorImpact(mode, year, mineral);
          if (response.success && response.data) {
            data = response.data;
            console.log(`Fetched data for ${mode} (year: ${year}, mineral: ${mineral}):`, data);
          } else {
            console.log(`Failed to fetch data for ${mode}:`, response);
            data = [];
          }
          break;
          
        case 'mineral-signals':
          if (mineral === 'All') {
            // Fetch data for all minerals and combine them
            const allMinerals = ['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite'];
            const allResponses = await Promise.all(
              allMinerals.map(min => getMineralSectorImpact(mode, year, min))
            );
            
            // Combine all successful responses
            const allData = [];
            allResponses.forEach((response, index) => {
              if (response.success && response.data) {
                allData.push(...response.data);
              }
            });
            
            // Create combined TreeMap data
            data = {
              name: "All Mineral Signals",
              children: allData
            };
            console.log(`Fetched combined data for all minerals:`, data);
          } else {
            // Fetch data for specific mineral
            const response = await getMineralSectorImpact(mode, year, mineral);
            if (response.success && response.data) {
              data = response.data;
              console.log(`Fetched data for ${mode} (year: ${year}, mineral: ${mineral}):`, data);
            } else {
              console.log(`Failed to fetch data for ${mode}:`, response);
              data = [];
            }
          }
          break;
          
        default:
          data = [];
      }
      
      console.log(`Setting treemap data for mode ${mode}:`, data);
      setTreemapData(data);
      
      // Always fetch alerts for the alerts panel
      const alertsResponse = await Promise.all(['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements'].map(mineral => 
        getDatabaseAlerts(mineral)
      ));
      
      const allAlerts = alertsResponse.flat();
        const filteredAlerts = allAlerts.filter(alert => {
          const category = alert.pestleCategory?.toLowerCase();
          return category === 'political' || category === 'economic' || category === 'legal';
        });
        
        const sortedAlerts = filteredAlerts
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);
        
        setAlerts(sortedAlerts);
      
      } catch (error) {
      console.error('Error fetching data for TreeMap mode:', mode, error);
      setTreemapData([]);
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    };
    
  // Fetch data on component mount and when mode changes
  useEffect(() => {
    fetchAvailableYearsAndMinerals();
    fetchDataForMode(treemapMode);
  }, [treemapMode]);

  // Handle year selection when switching to partner-risk mode
  useEffect(() => {
    if (treemapMode === 'partner-risk' && selectedYear === '2024') {
      // Switch to 2023 if 2024 is selected and we're in partner-risk mode
      setSelectedYear('2023');
    }
  }, [treemapMode, selectedYear]);

  // Handle mineral selection when switching to mineral-signals mode
  useEffect(() => {
    if (treemapMode === 'mineral-signals' && selectedMineralFilter !== 'All') {
      // Switch to "All" when entering mineral-signals mode
      setSelectedMineralFilter('All');
    }
  }, [treemapMode]);

  // Fetch data when year or mineral filter changes
  useEffect(() => {
    if (treemapMode === 'import-distribution' || treemapMode === 'export-distribution' || treemapMode === 'partner-risk' || treemapMode === 'mineral-signals') {
      fetchDataForMode(treemapMode, selectedYear, selectedMineralFilter);
    }
  }, [selectedYear, selectedMineralFilter]);

  // Color mapping based on risk levels and PESTLE colors
  const getRiskColor = (riskLevel) => {
    const level = riskLevel?.toLowerCase();
    switch (level) {
      case 'high': return '#ef4444'; // Red
      case 'medium': return '#f59e0b'; // Amber/Orange
      case 'low': return '#22c55e'; // Green
      case 'critical': // Map critical to high risk
      default: return '#6b7280'; // Gray
    }
  };

  // Get PESTLE category colors
  const getPestleColor = (category) => {
    const colors = {
      'Political': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
      'political': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
      'Economic': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
      'economic': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
      'Legal': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
      'legal': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
      'Social': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
      'social': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
      'Technological': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
      'technological': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
      'Environmental': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300',
      'environmental': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  };

  // Get severity colors
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Capitalize PESTLE category for display
  const capitalizePestleCategory = (category) => {
    if (!category) return 'Unknown';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };

  // Filter alerts by selected mineral
  const getFilteredAlerts = () => {
    if (!selectedMineral) {
      return alerts;
    }
    
    console.log('Filtering alerts for mineral:', selectedMineral);
    console.log('Total alerts available:', alerts.length);
    console.log('Sample alert structure:', alerts[0]);
    
    const filtered = alerts.filter(alert => {
      // Check if the alert message, title, or content contains the selected mineral
      const alertText = [
        alert.message,
        alert.title,
        alert.content,
        alert.summary,
        alert.mineral,
        alert.alert_message,
        alert.alert_type
      ].filter(Boolean).join(' ').toLowerCase();
      
      const mineralName = selectedMineral.toLowerCase();
      
      // Multiple matching strategies
      const matches = [
        // Direct text matches
        alertText.includes(mineralName),
        alert.mineral?.toLowerCase() === mineralName,
        alertText.includes(mineralName.replace(/\s+/g, '')),
        alertText.includes(mineralName.replace(/\s+/g, '-')),
        
        // Check for common variations
        alertText.includes('lithium') && mineralName === 'lithium',
        alertText.includes('lith') && mineralName === 'lithium',
        alertText.includes('cobalt') && mineralName === 'cobalt',
        alertText.includes('nickel') && mineralName === 'nickel',
        alertText.includes('rare earth') && mineralName === 'rare earth elements',
        alertText.includes('graphite') && mineralName === 'graphite',
        
        // Check if mineral is mentioned in any field
        Object.values(alert).some(value => 
          typeof value === 'string' && value.toLowerCase().includes(mineralName)
        ),
        
        // Special case: if alert mentions "critical minerals" and we're looking for a specific mineral,
        // we can consider it a match since it's relevant to critical minerals in general
        (alertText.includes('critical minerals') || alertText.includes('critical mineral')) && 
        ['lithium', 'cobalt', 'nickel', 'rare earth elements', 'graphite'].includes(mineralName)
      ];
      
      const hasMatch = matches.some(match => match);
      
      if (hasMatch) {
        console.log('Found matching alert:', alert);
      }
      
      return hasMatch;
    });
    
    console.log('Filtered alerts count:', filtered.length);
    
    // If no specific matches found, but we have alerts about critical minerals in general,
    // and the selected mineral is a critical mineral, show all alerts as they're all relevant
    if (filtered.length === 0 && alerts.length > 0) {
      const hasCriticalMineralsMention = alerts.some(alert => {
        const alertText = [
          alert.message,
          alert.title,
          alert.content,
          alert.summary
        ].filter(Boolean).join(' ').toLowerCase();
        return alertText.includes('critical minerals') || alertText.includes('critical mineral');
      });
      
      if (hasCriticalMineralsMention && ['lithium', 'cobalt', 'nickel', 'rare earth elements', 'graphite'].includes(selectedMineral.toLowerCase())) {
        console.log('No specific mineral matches found, but showing all critical minerals alerts as they are relevant');
        return alerts;
      }
    }
    
    return filtered;
  };

  // Function to shorten industry names for better display
  const shortenIndustryName = (industryName) => {
    if (!industryName) return '';
    
    const abbreviations = {
      'Automotive': 'Auto',
      'Electronics': 'Electronics',
      'Energy': 'Energy',
      'Chemical': 'Chemical',
      'Metallurgy': 'Metal',
      'Aerospace': 'Aero',
      'Defense': 'Defense',
      'Telecommunications': 'Telecom',
      'Healthcare': 'Health',
      'Construction': 'Construct',
      'Manufacturing': 'Manufact',
      'Transportation': 'Transport',
      'Agriculture': 'Agri',
      'Textiles': 'Textiles',
      'Pharmaceuticals': 'Pharma',
      'Renewable Energy': 'Renewable',
      'Oil & Gas': 'Oil & Gas',
      'Mining': 'Mining',
      'Steel': 'Steel',
      'Aluminum': 'Aluminum',
      'Copper': 'Copper',
      'Zinc': 'Zinc',
      'Lead': 'Lead',
      'Tin': 'Tin',
      'Nickel': 'Nickel',
      'Cobalt': 'Cobalt',
      'Lithium': 'Lithium',
      'Rare Earth Elements': 'REE',
      'Platinum Group Metals': 'PGM',
      'Precious Metals': 'Precious',
      'Base Metals': 'Base',
      'Industrial Minerals': 'Industrial',
      'Construction Materials': 'Construct',
      'Ceramics': 'Ceramics',
      'Glass': 'Glass',
      'Semiconductors': 'Semi',
      'Batteries': 'Batteries',
      'Solar': 'Solar',
      'Wind': 'Wind',
      'Nuclear': 'Nuclear',
      'Hydroelectric': 'Hydro',
      'Geothermal': 'Geo',
      'Biomass': 'Biomass',
      'Fuel Cells': 'Fuel Cells',
      'Energy Storage': 'Storage',
      'Electric Vehicles': 'EV',
      'Hybrid Vehicles': 'Hybrid',
      'Consumer Electronics': 'Consumer',
      'Mobile Devices': 'Mobile',
      'Computers': 'Computers',
      'Telecommunications Equipment': 'Telecom',
      'Medical Devices': 'Medical',
      'Aerospace Components': 'Aero',
      'Defense Systems': 'Defense',
      'Industrial Equipment': 'Industrial',
      'Machinery': 'Machinery',
      'Tools': 'Tools',
      'Instruments': 'Instruments',
      'Sensors': 'Sensors',
      'Actuators': 'Actuators',
      'Motors': 'Motors',
      'Generators': 'Generators',
      'Transformers': 'Transformers',
      'Cables': 'Cables',
      'Wires': 'Wires',
      'Connectors': 'Connectors',
      'Switches': 'Switches',
      'Relays': 'Relays',
      'Circuit Breakers': 'Breakers',
      'Fuses': 'Fuses',
      'Capacitors': 'Capacitors',
      'Resistors': 'Resistors',
      'Inductors': 'Inductors',
      'Diodes': 'Diodes',
      'Transistors': 'Transistors',
      'Integrated Circuits': 'ICs',
      'Microprocessors': 'Microprocessors',
      'Memory Chips': 'Memory',
      'Storage Devices': 'Storage',
      'Displays': 'Displays',
      'Screens': 'Screens',
      'Panels': 'Panels',
      'Modules': 'Modules',
      'Assemblies': 'Assemblies',
      'Subassemblies': 'Subassemblies',
      'Components': 'Components',
      'Parts': 'Parts',
      'Materials': 'Materials',
      'Raw Materials': 'Raw',
      'Processed Materials': 'Processed',
      'Finished Products': 'Finished',
      'Semi-finished Products': 'Semi-finished',
      'Intermediate Products': 'Intermediate',
      'By-products': 'By-products',
      'Waste Products': 'Waste',
      'Recycled Materials': 'Recycled',
      'Secondary Materials': 'Secondary',
      'Tertiary Materials': 'Tertiary',
      'Quaternary Materials': 'Quaternary',
      'Other Industries': 'Other',
      'Unknown': 'Unknown',
      'Not Specified': 'Not Specified',
      'N/A': 'N/A',
      'TBD': 'TBD',
      'To Be Determined': 'TBD'
    };
    
    // Check for exact match first
    if (abbreviations[industryName]) {
      return abbreviations[industryName];
    }
    
    // Check for partial matches (case insensitive)
    const lowerIndustry = industryName.toLowerCase();
    for (const [full, short] of Object.entries(abbreviations)) {
      if (lowerIndustry.includes(full.toLowerCase()) || full.toLowerCase().includes(lowerIndustry)) {
        return short;
      }
    }
    
    // If no match found, truncate to reasonable length
    return industryName.length > 12 ? industryName.substring(0, 12) + '...' : industryName;
  };

  // D3 TreeMap rendering effect
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 600;
    const height = 384; // h-96 = 24rem = 384px

    // Clear previous SVG
    d3.select(svgRef.current).selectAll("*").remove();

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("background", "#374151");

    // Transform data based on current mode
    let currentTreeMapData;
    switch (treemapMode) {
      case 'mineral-sector':
        // The API already returns data in TreeMap format, so use it directly
        if (mineralSectorData && mineralSectorData.children) {
          // Filter by selected industries if any are selected
          if (selectedIndustries && selectedIndustries.length > 0) {
            const filteredChildren = mineralSectorData.children.filter(industry => 
              selectedIndustries.includes(industry.name)
            );
            currentTreeMapData = {
              ...mineralSectorData,
              children: filteredChildren
            };
          } else {
            currentTreeMapData = mineralSectorData;
          }
        } else {
          currentTreeMapData = { name: "No Data", children: [] };
        }
        break;
      case 'import-distribution':
      case 'export-distribution':
      case 'partner-risk':
      case 'mineral-signals':
        // The API already returns data in TreeMap format, so use it directly
        if (treemapData && treemapData.children) {
          currentTreeMapData = treemapData;
        } else {
          currentTreeMapData = { name: "No Data", children: [] };
        }
        break;
      default:
        currentTreeMapData = transformDataToTreeMap(mineralSectorData, selectedIndustries);
    }
    
    console.log('TreeMap Debug Info:', {
      mineralSectorData: mineralSectorData,
      selectedIndustries: selectedIndustries,
      currentTreeMapData: currentTreeMapData,
      hasChildren: currentTreeMapData?.children?.length > 0
    });

    // Check if we have valid data to render
    if (!currentTreeMapData || !currentTreeMapData.children || currentTreeMapData.children.length === 0) {
      // Clear the SVG and return early if no data
      console.log('No data to render in treemap');
      svg.selectAll("*").remove();
      return;
    }

    // Create hierarchy
    const root = d3.hierarchy(currentTreeMapData)
      .sum(d => d.value || 0)
      .sort((a, b) => b.value - a.value);

    // Create treemap layout
    const treemap = d3.treemap()
      .size([width, height])
      .padding(2)
      .round(true);

    // Generate the treemap
    treemap(root);

    // Handle different data structures based on TreeMap mode
    if (treemapMode === 'partner-risk') {
      // For partner risk, we have a nested structure: risk levels -> countries -> minerals
      // We need to flatten it to show countries directly
      const flattenedData = [];
      
      if (currentTreeMapData && currentTreeMapData.children) {
        currentTreeMapData.children.forEach(riskLevel => {
          if (riskLevel && riskLevel.children) {
            riskLevel.children.forEach(country => {
              if (country && country.name) {
                flattenedData.push({
                  ...country,
                  riskLevelName: riskLevel.name || 'Unknown',
                  value: country.value || 0,
                  riskLevel: country.riskLevel || 'medium',
                  children: country.children || []
                });
              }
            });
          }
        });
      }
      
      console.log('Partner Risk - Flattened data:', flattenedData);
      
      if (flattenedData.length === 0) {
        console.log('No partner risk data to render');
        return;
      }
      
      // Create hierarchy from flattened data
      const flattenedRoot = d3.hierarchy({
        name: "Partner Risk Distribution",
        children: flattenedData
      })
      .sum(d => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
      
      // Generate the treemap for flattened data
      treemap(flattenedRoot);
      
      // Create country nodes
      const leaf = svg.selectAll("g")
        .data(flattenedRoot.children || [])
        .enter().append("g")
        .attr("transform", d => `translate(${d.x0},${d.y0})`);

      // Add rectangles for countries
      leaf.append("rect")
        .attr("id", d => `rect-${d.data.name}`)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", d => getRiskColor(d.data.riskLevel))
        .attr("stroke", d => selectedMineral === d.data.name ? "#3b82f6" : "#ffffff")
        .attr("stroke-width", d => selectedMineral === d.data.name ? 4 : 2)
        .style("cursor", "pointer")
        .on("mouseover", function(event, d) {
          d3.select(this).attr("opacity", 0.8);
          setHoveredCell(d.data.name);
          setTooltip({
            visible: true,
            data: { 
              ...d.data, 
              sector: "Partner Risk Distribution",
              riskLevel: d.data.riskLevelName,
              riskScore: d.data.value,
              tradeVolume: d.data.tradeVolume,
              indiaRole: d.data.indiaRole
            },
            x: event.pageX,
            y: event.pageY
          });
        })
        .on("mouseout", function(event, d) {
          d3.select(this).attr("opacity", 1);
          setHoveredCell(null);
          setTooltip({ visible: false, data: null, x: 0, y: 0 });
        })
        .on("mousemove", function(event, d) {
          setTooltip(prev => ({
            ...prev,
            x: event.pageX,
            y: event.pageY
          }));
        })
        .on("click", function(event, d) {
          setSelectedMineral(d.data.name);
          console.log('Selected country:', d.data.name);
        });

      // Add country labels
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 16)
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .attr("fill", "#ffffff")
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => d.data.name);

      // Add risk score labels
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 30)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => `Risk: ${(d.data.value || 0).toFixed(1)}`);

      // Add risk level labels
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 44)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.8)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => d.data.riskLevelName || 'Unknown');

      // Add country boundaries
      leaf.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", "none")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3")
        .attr("opacity", 0.5);
        
    } else if (treemapMode === 'mineral-signals') {
      // For mineral-signals mode, handle the data structure appropriately
      // This mode might not have the same data structure as other modes
      console.log('Mineral Signals mode - Data structure:', currentTreeMapData);
      
      if (!currentTreeMapData || !currentTreeMapData.children || currentTreeMapData.children.length === 0) {
        console.log('No mineral signals data to render');
        return;
      }
      
      // Create country nodes (children of root, not leaves)
      const leaf = svg.selectAll("g")
        .data(root.children || [])
        .enter().append("g")
        .attr("transform", d => `translate(${d.x0},${d.y0})`);

      // Add rectangles for countries
      leaf.append("rect")
        .attr("id", d => `rect-${d.data.name}`)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", d => getRiskColor(d.data.riskLevel || 'medium'))
        .attr("stroke", d => selectedMineral === d.data.name ? "#3b82f6" : "#ffffff")
        .attr("stroke-width", d => selectedMineral === d.data.name ? 4 : 2)
        .style("cursor", "pointer")
        .on("mouseover", function(event, d) {
          d3.select(this).attr("opacity", 0.8);
          setHoveredCell(d.data.name);
          setTooltip({
            visible: true,
            data: { 
              ...d.data, 
              sector: "Mineral-wise Signals",
              value: d.data.value || 0,
              riskLevel: d.data.riskLevel || 'medium'
            },
            x: event.pageX,
            y: event.pageY
          });
        })
        .on("mouseout", function(event, d) {
          d3.select(this).attr("opacity", 1);
          setHoveredCell(null);
          setTooltip({ visible: false, data: null, x: 0, y: 0 });
        })
        .on("mousemove", function(event, d) {
          setTooltip(prev => ({
            ...prev,
            x: event.pageX,
            y: event.pageY
          }));
        })
        .on("click", function(event, d) {
          setSelectedMineral(d.data.name);
          console.log('Selected mineral signal:', d.data.name);
        });

      // Add labels
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 16)
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .attr("fill", "#ffffff")
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => d.data.name);

      // Add value labels with proper null checks
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 30)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => {
          const value = d.data.value;
          if (value === null || value === undefined) {
            return 'N/A';
          }
          return `${(value || 0).toFixed(1)}`;
        });

      // Add country boundaries
      leaf.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", "none")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3")
        .attr("opacity", 0.5);
        
    } else if (treemapMode === 'mineral-sector') {
      // For mineral-sector mode, render industries with their minerals
      console.log('Rendering mineral-sector mode with data:', currentTreeMapData);
      
      // Create industry nodes (children of root)
      const leaf = svg.selectAll("g")
        .data(root.children || [])
        .enter().append("g")
        .attr("transform", d => `translate(${d.x0},${d.y0})`);

      // Add rectangles for industries
      leaf.append("rect")
        .attr("id", d => `rect-${d.data.name}`)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", d => getRiskColor(d.data.riskLevel || 'medium'))
        .attr("stroke", d => selectedMineral === d.data.name ? "#3b82f6" : "#ffffff")
        .attr("stroke-width", d => selectedMineral === d.data.name ? 4 : 2)
        .style("cursor", "pointer")
        .on("mouseover", function(event, d) {
          d3.select(this).attr("opacity", 0.8);
          setHoveredCell(d.data.name);
          setTooltip({
            visible: true,
            data: { 
              ...d.data, 
              sector: "Mineral-Sector Impact",
              industry: d.data.name,
              value: d.data.value,
              riskLevel: d.data.riskLevel || 'medium'
            },
            x: event.pageX,
            y: event.pageY
          });
        })
        .on("mouseout", function(event, d) {
          d3.select(this).attr("opacity", 1);
          setHoveredCell(null);
          setTooltip({ visible: false, data: null, x: 0, y: 0 });
        })
        .on("mousemove", function(event, d) {
          setTooltip(prev => ({
            ...prev,
            x: event.pageX,
            y: event.pageY
          }));
        })
        .on("click", function(event, d) {
          setSelectedMineral(d.data.name);
          console.log('Selected industry:', d.data.name);
        });

      // Add industry labels
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 16)
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .attr("fill", "#ffffff")
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => d.data.name);

      // Add associated mineral names
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 32)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => {
          if (d.data.children && d.data.children.length > 0) {
            const mineralNames = d.data.children.map(child => child.name).join(", ");
            // Truncate if too long to fit in the cell
            const maxLength = Math.floor((d.x1 - d.x0) / 8); // Rough character limit based on cell width
            return mineralNames.length > maxLength ? 
              mineralNames.substring(0, maxLength) + "..." : 
              mineralNames;
          }
          return "No minerals";
        });

      // Add risk level labels (moved up since we removed risk score)
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 48)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.8)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => {
          const riskLevel = d.data.riskLevel;
          if (riskLevel) {
            return riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1) + ' Risk';
          }
          return 'Medium Risk';
        });

      // Add industry boundaries
      leaf.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", "none")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3")
        .attr("opacity", 0.5);
        
    } else {
      // Original code for other modes (import-distribution, export-distribution, etc.)
      // Create country nodes (children of root, not leaves)
      const leaf = svg.selectAll("g")
        .data(root.children || [])
        .enter().append("g")
        .attr("transform", d => `translate(${d.x0},${d.y0})`);

      // Add rectangles for countries
      leaf.append("rect")
        .attr("id", d => `rect-${d.data.name}`)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", d => {
          // Debug logging for USA specifically
          if (d.data.name === 'USA' || d.data.name === 'United States') {
            console.log('DEBUG: USA D3 rendering:', {
              country: d.data.name,
              riskLevel: d.data.riskLevel,
              color: getRiskColor(d.data.riskLevel),
              fullData: d.data
            });
          }
          return getRiskColor(d.data.riskLevel);
        })
        .attr("stroke", d => selectedMineral === d.data.name ? "#3b82f6" : "#ffffff")
        .attr("stroke-width", d => selectedMineral === d.data.name ? 4 : 2)
        .style("cursor", "pointer")
        .on("mouseover", function(event, d) {
          d3.select(this).attr("opacity", 0.8);
          setHoveredCell(d.data.name);
          setTooltip({
            visible: true,
            data: { 
              ...d.data, 
              sector: "Import Distribution",
              percentage: d.data.value,
              quantity: d.data.quantity,
              quantityFormatted: d.data.quantity >= 1000000 ? 
                `${(d.data.quantity / 1000000).toFixed(1)}M tonnes` :
                d.data.quantity >= 1000 ? 
                `${(d.data.quantity / 1000).toFixed(1)}K tonnes` :
                `${d.data.quantity.toFixed(0)} tonnes`
            },
            x: event.pageX,
            y: event.pageY
          });
        })
        .on("mouseout", function(event, d) {
          d3.select(this).attr("opacity", 1);
          setHoveredCell(null);
          setTooltip({ visible: false, data: null, x: 0, y: 0 });
        })
        .on("mousemove", function(event, d) {
          setTooltip(prev => ({
            ...prev,
            x: event.pageX,
            y: event.pageY
          }));
        })
        .on("click", function(event, d) {
          // Select the mineral when clicked
          setSelectedMineral(d.data.name);
          console.log('Selected mineral:', d.data.name);
        });

      // Add country labels
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 16)
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .attr("fill", "#ffffff")
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => d.data.name);

      // Add percentage labels for countries
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 30)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => {
          const value = d.data.value;
          if (value === null || value === undefined) {
            return 'N/A';
          }
          return `${(value || 0).toFixed(1)}%`;
        });

      // Add quantity labels for countries (in tonnes)
      leaf.append("text")
        .attr("x", 4)
        .attr("y", 44)
        .attr("font-size", "16px")
        .attr("fill", "#ffffff")
        .attr("opacity", 0.8)
        .attr("font-family", "Inter, system-ui, sans-serif")
        .text(d => {
          const quantity = d.data.quantity;
          if (quantity === null || quantity === undefined) {
            return 'N/A';
          }
          const qty = quantity || 0;
          if (qty >= 1000000) {
            return `${(qty / 1000000).toFixed(1)}M tonnes`;
          } else if (qty >= 1000) {
            return `${(qty / 1000).toFixed(1)}K tonnes`;
          } else {
            return `${qty.toFixed(0)} tonnes`;
          }
        });

      // Add country boundaries (dotted borders)
      leaf.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", d => d.x1 - d.x0)
        .attr("height", d => d.y1 - d.y0)
        .attr("fill", "none")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3")
        .attr("opacity", 0.5);
    }

  }, [mineralSectorData, selectedIndustries, selectedMineral, treemapMode, treemapData]);

  // Industry filter functions
  const handleIndustryToggle = (industry) => {
    setSelectedIndustries(prev => {
      if (prev.includes(industry)) {
        return prev.filter(ind => ind !== industry);
      } else {
        return [...prev, industry];
      }
    });
  };

  const handleSelectAllIndustries = () => {
    if (availableIndustries && availableIndustries.length > 0) {
      setSelectedIndustries([...availableIndustries]);
    }
  };

  const handleClearAllIndustries = () => {
    setSelectedIndustries([]);
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      // Trigger re-render on resize
      if (containerRef.current) {
        // Small delay to ensure container dimensions are updated
        setTimeout(() => {
          const event = new Event('resize');
          window.dispatchEvent(event);
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle clicking outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.relative')) {
        setShowDropdown(false);
      }
      if (showYearDropdown && !event.target.closest('.relative')) {
        setShowYearDropdown(false);
      }
      if (showMineralDropdown && !event.target.closest('.relative')) {
        setShowMineralDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, showYearDropdown, showMineralDropdown]);





  return (
    <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6 h-full">
      {/* CSS for line clamp */}
      <style jsx>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          {/* TreeMap Mode - Fixed to Mineral-Sector Impact */}
          <div className="flex items-center space-x-2 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600">
            <span className="text-sm font-medium">
              Mineral-Sector Impact
            </span>
          </div>

          {/* Mineral Selection Dropdown - Hidden since we only have mineral-sector mode */}
          {false && (
            <div className="relative">
              <button
                onClick={() => setShowMineralDropdown(!showMineralDropdown)}
                className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg border border-gray-600 transition-colors"
              >
                <span className="text-sm font-medium">Mineral: {selectedMineralFilter}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showMineralDropdown && (
                <div className="absolute left-0 mt-2 w-48 bg-gray-700 border border-gray-600 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    {(treemapMode === 'mineral-signals' ? availableMineralsWithAll : availableMinerals).map((mineral) => (
                      <button
                        key={mineral}
                        onClick={() => {
                          setSelectedMineralFilter(mineral);
                          setShowMineralDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-sm hover:bg-gray-600 transition-colors ${
                          selectedMineralFilter === mineral ? 'bg-gray-600 text-white' : 'text-gray-300'
                        }`}
                      >
                        {mineral}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Year Selection Dropdown - Hidden since we only have mineral-sector mode */}
          {false && (
            <div className="relative">
              <button
                onClick={() => setShowYearDropdown(!showYearDropdown)}
                className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg border border-gray-600 transition-colors"
              >
                <span className="text-sm font-medium">Year: {selectedYear}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showYearDropdown && (
                <div className="absolute left-0 mt-2 w-32 bg-gray-700 border border-gray-600 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    {getFilteredYears().map((year) => (
                      <button
                        key={year}
                        onClick={() => {
                          setSelectedYear(year.toString());
                          setShowYearDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-sm hover:bg-gray-600 transition-colors ${
                          selectedYear === year.toString() ? 'bg-gray-600 text-white' : 'text-gray-300'
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* <div className="flex items-center space-x-3">
          <div>
            <h3 className="text-lg font-semibold text-white">TreeMap Analysis</h3>
            <p className="text-sm text-gray-300">
              {treemapModes.find(mode => mode.value === treemapMode)?.label || 'Geopolitical and supply chain risks by sector'}
            </p>
          </div>
        </div> */}
      </div>

      {/* Primary Industry Filter - Only show for mineral-sector mode */}
      {treemapMode === 'mineral-sector' && (
      <div className="mb-4 p-3 bg-gray-700 rounded-lg border border-gray-600">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            Filter by Primary Industry
          </h4>
          {availableIndustries.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={handleSelectAllIndustries}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
              >
                Select All
              </button>
              <button
                onClick={handleClearAllIndustries}
                className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
        
        {availableIndustries.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {availableIndustries && availableIndustries.map((industry, index) => {
              const isSelected = selectedIndustries && selectedIndustries.includes(industry);

              return (
                <button
                  key={index}
                  onClick={() => handleIndustryToggle(industry)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white border border-blue-500'
                      : 'bg-gray-600 text-gray-300 border border-gray-500 hover:bg-gray-500'
                  }`}
                >
                  <span className="truncate max-w-[200px]" title={industry}>{shortenIndustryName(industry)}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-gray-400 text-sm">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span>Loading industry data...</span>
              </div>
              <p className="text-xs text-gray-500">
                Loading data from mineral_sector_impact table. If this persists, check that the API endpoint /api/mineral-sector-impact is working.
              </p>
            </div>
          </div>
        )}
        
        {availableIndustries.length > 0 && (
          <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
            <span>
              Showing {selectedIndustries.length} of {availableIndustries.length} industries
            </span>
            {selectedIndustries.length === 0 && (
              <span className="text-yellow-400 ml-2">⚠️ No industries selected - tree map will be empty</span>
            )}
          </div>
        )}
      </div>
      )}



      {/* Two-column layout: TreeMap on left, Alerts on right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[450px]">
        
        {/* Left Column: TreeMap */}
        <div className="lg:col-span-2">
          {/* Dynamic Legend based on TreeMap mode */}
          <div className="flex items-center space-x-4 mb-4 text-xs">
            {treemapMode === 'import-distribution' ? (
              // Import dependency levels legend (size = quantity, color = dependency %)
              <>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span className="text-gray-300">High Dependency (≥70%)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-amber-500 rounded"></div>
                  <span className="text-gray-300">Medium Dependency (40-69%)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-gray-300">Low Dependency (&lt;40%)</span>
                </div>
                <div className="text-xs text-gray-400 ml-4">
                  Size = Import Quantity | Color = Dependency %
                </div>
              </>
            ) : treemapMode === 'mineral-signals' ? (
              // PESTLE categories legend for signals
              <>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span className="text-gray-300">Political</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-orange-500 rounded"></div>
                  <span className="text-gray-300">Economic</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span className="text-gray-300">Social</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-gray-300">Technological</span>
                </div>
              </>
            ) : treemapMode === 'partner-risk' ? (
              // Risk levels legend for partner risk
              <>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span className="text-gray-300">High Risk</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-amber-500 rounded"></div>
              <span className="text-gray-300">Medium Risk</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-gray-300">Low Risk</span>
            </div>
              </>
            ) : (
              // Default risk levels legend
              <>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span className="text-gray-300">High Risk</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-amber-500 rounded"></div>
                  <span className="text-gray-300">Medium Risk</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-gray-300">Low Risk</span>
                </div>
              </>
            )}
          </div>

          {/* D3 Tree Map */}
          <div 
            ref={containerRef}
            className="h-96 border border-gray-600 rounded-lg bg-gray-700 relative overflow-hidden"
          >
            <svg 
              ref={svgRef}
              className="w-full h-full rounded-lg"
              style={{ overflow: 'hidden' }}
            />
            
            {/* Show message when no data available */}
            {((treemapMode === 'mineral-sector' && selectedIndustries.length === 0) || 
              (treemapMode !== 'mineral-sector' && (!treemapData || treemapData.length === 0))) && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">🔍</div>
                  <div>No data available</div>
                  <div className="text-sm text-gray-400 mt-2">
                    {treemapMode === 'mineral-sector' ? (
                      availableIndustries.length === 0 
                      ? "Mineral sector impact data not available. Please ensure the /api/mineral-sector-impact endpoint is working and the mineral_sector_impact table has data."
                      : "Select industries from the filters above to view data"
                    ) : (
                      `No ${treemapMode.replace('-', ' ')} data available. Check browser console for detailed error information.`
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Check browser console for detailed error information
                  </div>
                </div>
              </div>
            )}
            
            {/* Dynamic Custom Tooltip based on TreeMap mode */}
            {tooltip.visible && tooltip.data && (
              <div 
                className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm pointer-events-none"
                style={{
                  left: tooltip.x - 200,
                  top: tooltip.y - 100,
                  transform: 'translateY(-100%)'
                }}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: getRiskColor(tooltip.data.riskLevel) }}
                  ></div>
                  <h4 className="font-semibold text-gray-900">{tooltip.data.name}</h4>
                  {tooltip.data.riskLevel && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    tooltip.data.riskLevel === 'high' ? 'bg-red-100 text-red-700' :
                    tooltip.data.riskLevel === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {tooltip.data.riskLevel} risk
                  </span>
                  )}
                </div>
                
                {/* Dynamic content based on TreeMap mode */}
                {treemapMode === 'mineral-sector' && (
                  <>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Sector:</strong> {tooltip.data.sector}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Applications:</strong> {tooltip.data.applications}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Impact Score:</strong> {tooltip.data.value}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Recent Signals:</strong> {tooltip.data.signals}
                </p>
                  </>
                )}
                
                {treemapMode === 'import-distribution' && (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Import Share:</strong> {tooltip.data.importPercentage?.toFixed(1)}%
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Import Quantity:</strong> {tooltip.data.volume?.toLocaleString()} MT
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Price:</strong> ${tooltip.data.price?.toLocaleString()}/ton
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Size = Quantity | Color = Dependency %
                    </p>
                  </>
                )}
                
                {treemapMode === 'export-distribution' && (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Export Share:</strong> {tooltip.data.exportPercentage?.toFixed(1)}%
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Export Value:</strong> ${tooltip.data.exportValue?.toLocaleString()}
                    </p>
                  </>
                )}
                
                {treemapMode === 'partner-risk' && (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Risk Score:</strong> {tooltip.data.value?.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Trade Volume:</strong> {tooltip.data.tradeVolume?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>India Role:</strong> {tooltip.data.indiaRole}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Supply Chain Risk:</strong> {tooltip.data.supplyChainRisk?.toFixed(2)}
                    </p>
                  </>
                )}
                
                {treemapMode === 'mineral-signals' && (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Signal Count:</strong> {tooltip.data.signalCount}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Risk Score:</strong> {tooltip.data.riskScore?.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Impact Level:</strong> {tooltip.data.impactLevel}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Alerts */}
        <div className="lg:col-span-1">
          <div className="h-full">
            {/* Alerts Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <h4 className="text-md font-semibold text-white">
                  {selectedMineral ? `${selectedMineral} Alerts` : 'Recent Alerts'}
                </h4>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded-full">
                  {getFilteredAlerts().length} alerts
                </span>
                {selectedMineral && (
                  <button
                    onClick={() => setSelectedMineral(null)}
                    className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded border border-blue-400 hover:bg-blue-400/10"
                    title="Clear mineral filter"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Minerals Filter Info */}
            <div className="mb-3 text-xs text-gray-400">
              <p className="mt-1">Political, Economic & Legal only</p>
            </div>

            {/* Selected Mineral Info */}
            {selectedMineral && (
              <div className="mb-3 p-2 bg-blue-900/20 border border-blue-700 rounded-lg">
                <div className="text-xs text-blue-300">
                  <span className="font-medium">Selected:</span> {selectedMineral}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Showing {getFilteredAlerts().length} of {alerts.length} total alerts
                  {getFilteredAlerts().length === alerts.length && getFilteredAlerts().length > 0 && (
                    <span className="text-yellow-400 ml-2">(showing all critical minerals alerts)</span>
                  )}
                </div>
                {/* Debug info - remove in production */}
                <details className="mt-2 text-xs text-gray-500">
                  <summary className="cursor-pointer">Debug: View alert data</summary>
                  <div className="mt-1 p-2 bg-gray-800 rounded text-xs overflow-auto max-h-32">
                    <div className="mb-2">Sample alerts:</div>
                    {alerts.slice(0, 2).map((alert, idx) => (
                      <div key={idx} className="mb-1 p-1 bg-gray-700 rounded">
                        <div>Message: {alert.message?.substring(0, 50)}...</div>
                        <div>Mineral: {alert.mineral || 'N/A'}</div>
                        <div>Title: {alert.title?.substring(0, 30)}...</div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}

            {/* Alerts List */}
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4 text-gray-400">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2"></div>
                  <div className="text-sm">Loading alerts...</div>
                </div>
              ) : getFilteredAlerts().length > 0 ? (
                getFilteredAlerts().map((alert, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${getSeverityColor(alert.pestle_risk_level || alert.severity)}`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-gray-300">{alert.date}</div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getPestleColor(alert.pestleCategory)}`}>
                            {capitalizePestleCategory(alert.pestleCategory)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-200 mb-2 line-clamp-3">{alert.message}</div>
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            alert.pestle_risk_level?.toLowerCase() === 'critical' ? 'bg-red-800 text-red-100' :
                            alert.pestle_risk_level?.toLowerCase() === 'high' ? 'bg-red-700 text-red-100' :
                            alert.pestle_risk_level?.toLowerCase() === 'medium' ? 'bg-yellow-700 text-yellow-100' :
                            'bg-green-700 text-green-100'
                          }`}>
                            {alert.pestle_risk_level || alert.severity}
                          </span>
                          {alert.sourceUrl && (
                            <a 
                              href={alert.sourceUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              Source →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <div className="text-lg mb-2">📊</div>
                  <div className="text-sm">
                    {selectedMineral ? `No alerts found for ${selectedMineral}` : 'No alerts available'}
                  </div>
                  <div className="text-xs mt-1">
                    {selectedMineral 
                      ? `Try clicking on a different mineral in the treemap`
                      : 'Political, Economic & Legal alerts for critical minerals will appear here'
                    }
                  </div>
                  {selectedMineral && (
                    <div className="mt-3">
                      <button
                        onClick={() => setSelectedMineral(null)}
                        className="text-xs text-blue-400 hover:text-blue-300 underline mr-3"
                      >
                        Show all alerts
                      </button>
                      <button
                        onClick={() => {
                          // Temporary: show all alerts for debugging
                          console.log('Showing all alerts for debugging:', alerts);
                        }}
                        className="text-xs text-yellow-400 hover:text-yellow-300 underline"
                      >
                        Debug: Show all alerts
                      </button>
                    </div>
                  )}
                  
                  {/* Temporary debug section */}
                  {selectedMineral && alerts.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-800 rounded text-xs text-left">
                      <div className="text-yellow-400 mb-2">Debug: All available alerts:</div>
                      {alerts.slice(0, 3).map((alert, idx) => (
                        <div key={idx} className="mb-2 p-2 bg-gray-700 rounded">
                          <div className="text-gray-300">Message: {alert.message?.substring(0, 100)}...</div>
                          <div className="text-gray-400">Mineral: {alert.mineral || 'N/A'}</div>
                          <div className="text-gray-400">Title: {alert.title || 'N/A'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MineralSectorTreeMap;
